import React, { useEffect, useState, useMemo } from "react";
import { Calendar, Phone, Clock, MapPin, User, ChevronRight, MessageCircle } from "lucide-react";
import { useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { LIST_USER_MEETINGS, GET_CRITICAL_LEADS, CURRENT_USER_PROFIILE, CURRENT_USER_ACCOUNT, GET_ALL_LEAD_CATEGORIES } from "../../services/userApi";
import LeadMetrics from "../../Home/LeadMetrics";
import CriticalLeads from "./CriticalLeads";
import BackButton from "../../reusableField/BackButton";
import "../../Home/global.css";

const LDMSDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(null);

  const { today, endDate } = useMemo(() => {
    const getFormattedDate = (date) => date.toISOString().replace(/\.\d{3}Z$/, "+00:00");
    const todayDate = getFormattedDate(new Date());
    const dayAfterTomorrow = new Date();
    dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 2);
    return { today: todayDate, endDate: getFormattedDate(dayAfterTomorrow) };
  }, []);

  const { loading: accountsLoading, error: accountsError, data: accountsData } = useQuery(CURRENT_USER_ACCOUNT, { fetchPolicy: "cache-first" });
  const { loading: profileLoading, error: profileError, data: profileData } = useQuery(CURRENT_USER_PROFIILE, { fetchPolicy: "cache-first" });

  const accountdata = accountsData?.currentUserAccount;
  const accoundId = accountsData?.currentUserAccount?.id;
  const userdata = profileData?.currentUserProfile;
  const userId = profileData?.currentUserProfile?.id;
  const jobroleId = userdata?.roleId;

  const { data: getAllCategories } = useQuery(GET_ALL_LEAD_CATEGORIES, { variables: { roleId: jobroleId }, skip: !jobroleId }, { fetchPolicy: "cache-first" });

  const { data: schedulelist, loading: scheduleloading } = useQuery(LIST_USER_MEETINGS, {
    variables: { fromDate: today, toDate: endDate },
    skip: !today || !endDate,
    fetchPolicy: "cache-first",
  });

  const { data: criticalLeadsData, loading: criticalLeadsLoading, error: criticalLeadsError } = useQuery(GET_CRITICAL_LEADS, {
    variables: { jobroleId: jobroleId, leadCategoryId: activeTab, userId: userId, records: 10 },
    skip: !jobroleId || !userId || !activeTab,
    fetchPolicy: "cache-first",
    // nextFetchPolicy: "cache-first",
  });

  useEffect(() => {
    if (getAllCategories?.getAllLeadCategories?.length > 0 && !activeTab) {
      const firstLeadCategory = getAllCategories.getAllLeadCategories.find((cat) => cat.isLead === true);
      if (firstLeadCategory) setActiveTab(firstLeadCategory.id);
    }
  }, [getAllCategories, activeTab]);

  const processedSchedule = useMemo(() => {
    if (!schedulelist?.listUserMeetings) return [];
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const todayEnd = new Date();
    todayEnd.setHours(23, 59, 59, 999);
    return schedulelist.listUserMeetings
      .filter((item) => {
        const meetingStart = new Date(item.meeting.meetingStartDatetime);
        return meetingStart >= todayStart && meetingStart <= todayEnd;
      })
      .map((item) => {
        const meetingStart = new Date(item.meeting.meetingStartDatetime);
        const meetingEnd = new Date(item.meeting.meetingEndDatetime);
        const formatTime = (date) => date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
        const leadName = item.leadDetails.leadFormDetails?.questions?.[0]?.userResponse?.response || item.leadDetails.leadFormDetails?.name || "Unnamed Lead";
        const companyName = item.leadDetails.leadFormDetails?.questions?.[1]?.userResponse?.response;
        const activityMethod = item.activity.activityMethod.toLowerCase();
        let type = "reminder";
        if (activityMethod === "call") type = "call";
        else if (activityMethod === "meeting") type = "meeting";
        else if (activityMethod === "whatsapp") type = "whatsapp";
        const titleName = companyName ? `${leadName} (${companyName})` : leadName;
        return {
          id: item.id,
          title: `${item.activity.activityName} - ${titleName}`,
          time: `${formatTime(meetingStart)} - ${formatTime(meetingEnd)}`,
          type: type,
          status: item.activityStatus?.statusName || "Scheduled",
          remarks: item.remarks,
          location: item.meeting.location,
          leadId: item.leadDetails.id,
          activityName: item.activity.activityName,
        };
      })
      .sort((a, b) => {
        const itemA = schedulelist.listUserMeetings.find(i => i.id === a.id);
        const itemB = schedulelist.listUserMeetings.find(i => i.id === b.id);
        return new Date(itemA.meeting.meetingStartDatetime) - new Date(itemB.meeting.meetingStartDatetime);
      });
  }, [schedulelist]);

  // Calculate schedule summary
  const scheduleSummary = useMemo(() => {
    const summary = {
      total: processedSchedule.length,
      calls: 0,
      meetings: 0,
      whatsapp: 0,
      reminders: 0
    };

    processedSchedule.forEach(item => {
      if (item.type === 'call') summary.calls++;
      else if (item.type === 'meeting') summary.meetings++;
      else if (item.type === 'whatsapp') summary.whatsapp++;
      else if (item.type === 'reminder') summary.reminders++;
    });

    return summary;
  }, [processedSchedule]);

  const getTypeIcon = (type) => {
    switch (type) {
      case "call": return <Phone className="w-3 h-3" />;
      case "meeting": return <User className="w-3 h-3" />;
      case "reminder": return <Clock className="w-3 h-3" />;
      case "whatsapp": return <MessageCircle className="w-3 h-3" />;
      case "visit": return <MapPin className="w-3 h-3" />;
      default: return <Calendar className="w-3 h-3" />;
    }
  };

  const calculateDaysSince = (lead) => {
    const dateToUse = lead.leadLastStageMoment?.createdAt || lead.leadCreatedAt;
    if (!dateToUse) return null;
    const stageDate = new Date(dateToUse);
    const currentDate = new Date();
    stageDate.setHours(0, 0, 0, 0);
    currentDate.setHours(0, 0, 0, 0);
    const diffTime = currentDate - stageDate;
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
  };

  const handleViewAllLeads = () => navigate("/leads");

  const parseAndExtractValue = (response, viewValue) => {
    if (!response) return "N/A";
    if (!viewValue) return response;
    try {
      let parsedResponse;
      if (typeof response === "string") {
        if (response.startsWith("{") && response.includes(":")) {
          const jsonString = response.replace(/(\w+):/g, '"$1":').replace(/: ([^,}]+)/g, ': "$1"');
          try {
            parsedResponse = JSON.parse(jsonString);
          } catch {
            const match = response.match(/response:\s*([^,}]+)/);
            if (match) return match[1].trim();
            return response;
          }
        } else {
          parsedResponse = JSON.parse(response);
        }
      } else {
        parsedResponse = response;
      }
      if (parsedResponse && typeof parsedResponse === "object") return parsedResponse[viewValue] || response;
      return response;
    } catch (error) {
      return response;
    }
  };

  const leaddetails = (leadId) => {
    navigate(`/leads/leadprofile`, { state: { leadId: leadId, returnPage: 1, leadCategoryId: activeTab } });
  };

  const handleStageClick = (stageId) => {
    navigate(`/leads`, { state: { stageId, roleId: jobroleId, leadCategoryId: activeTab } });
  };

  const handleStatusClick = (statusId) => {
    navigate(`/leads`, { state: { statusId, roleId: jobroleId, leadCategoryId: activeTab } });
  };

  const handleScheduleItemClick = (item) => {
    if (item.leadId) leaddetails(item.leadId);
  };

  const criticalLeads = criticalLeadsData?.getCriticalLeads || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 mt-[70px] ml-[70px] global">
      <div className="max-w-7xl mx-auto">
        {/* <div style={{ marginBottom: "16px" }}>
          <BackButton label="Back" />
        </div> */}
        {getAllCategories?.getAllLeadCategories && (
          <div className="flex justify-center mb-4">
            <div className="bg-gray-200 rounded-full p-1 inline-flex">
              {getAllCategories.getAllLeadCategories.filter((category) => category.isLead === true).map((category) => (
                <button key={category.id} onClick={() => setActiveTab(category.id)} className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${activeTab === category.id ? "bg-white text-red-500 shadow-sm" : "text-gray-600 hover:text-gray-800"}`}>{category.categoryName}</button>
              ))}
            </div>
          </div>
        )}

        {/* Schedule Details and Metrics Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          <div className="bg-white rounded-lg p-4 relative shadow-md" style={{ minHeight: "350px", maxHeight: "450px", overflowY: "auto" }}>
            {scheduleloading && !schedulelist && (
              <div className="absolute inset-0 bg-white bg-opacity-90 rounded-lg flex justify-center items-center z-10">
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#dc3545]"></div>
                  <p className="ml-2 text-sm text-gray-600">Loading schedule...</p>
                </div>
              </div>
            )}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
                <Calendar className="w-5 h-5" style={{ color: "#c12328" }} />
                Today's Schedule
              </h2>
            </div>
            {processedSchedule.length > 0 ? (
              <div className="space-y-2">
                {processedSchedule.map((item) => (
                  <div key={item.id} onClick={() => handleScheduleItemClick(item)} className="border-l-4 pl-3 py-2 rounded-r-lg bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer" style={{ borderLeftColor: "#c12328" }}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-2 flex-1">
                        <div className="mt-0.5 p-1.5 rounded-lg" style={{ backgroundColor: "#F69193" }}>{getTypeIcon(item.type)}</div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-800 text-xs mb-0.5">{item.title}</h3>
                          <p className="text-xs text-gray-600 flex items-center gap-1"><Clock className="w-3 h-3" />{item.time}</p>
                          {item.remarks && <p className="text-xs text-gray-500 italic mt-0.5">{item.remarks}</p>}
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3" style={{ backgroundColor: "#fef3f3" }}>
                  <Calendar className="w-6 h-6" style={{ color: "#c12328" }} />
                </div>
                <h3 className="font-semibold text-gray-800 text-sm mb-1">Nothing Scheduled for Today</h3>
                <p className="text-xs text-gray-600 text-center">You have a clear schedule today. Time to focus on your leads!</p>
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg p-4 shadow-md" style={{ minHeight: "350px", maxHeight: "450px", overflowY: "auto" }}>
            <LeadMetrics jobRoleId={jobroleId} userId={userId} onStageClick={handleStageClick} onStatusClick={handleStatusClick} showStages={true} showStatus={true} />

            {/* Schedule Summary Cards */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <h3 className="text-xs font-semibold text-gray-700 mb-3">Today's Schedule Summary</h3>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-2.5 border border-red-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-red-600 font-medium">Total</p>
                      <h4 className="text-xl font-bold text-red-700">{scheduleSummary.total}</h4>
                    </div>
                    <Calendar className="w-6 h-6 text-red-500 opacity-70" />
                  </div>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-2.5 border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-blue-600 font-medium">Calls</p>
                      <h4 className="text-xl font-bold text-blue-700">{scheduleSummary.calls}</h4>
                    </div>
                    <Phone className="w-6 h-6 text-blue-500 opacity-70" />
                  </div>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-2.5 border border-purple-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-purple-600 font-medium">Meetings</p>
                      <h4 className="text-xl font-bold text-purple-700">{scheduleSummary.meetings}</h4>
                    </div>
                    <User className="w-6 h-6 text-purple-500 opacity-70" />
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-2.5 border border-green-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-green-600 font-medium">WhatsApp</p>
                      <h4 className="text-xl font-bold text-green-700">{scheduleSummary.whatsapp}</h4>
                    </div>
                    <MessageCircle className="w-6 h-6 text-green-500 opacity-70" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full">
          <CriticalLeads criticalLeads={criticalLeads} loading={criticalLeadsLoading && !criticalLeadsData} onLeadClick={leaddetails} onViewAllClick={handleViewAllLeads} calculateDaysSince={calculateDaysSince} parseAndExtractValue={parseAndExtractValue} />
        </div>
      </div>
    </div>
  );
};

export default LDMSDashboard;